import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

export interface ClientS3CredentialDTO {
  id: number;
  company_id: number;
  profile_name: string;
  s3_bucket: string;
  s3_region: string;
  access_key_id: string;
  secret_access_key: string;
  root_folder?: string | null;
  s3_endpoint?: string | null;
  is_active: number;
  created_by?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export class ClientS3ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ClientS3ApiError";
    this.status = status;
  }
}

const getAuthHeaders = () => {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  return {
    Authorization: `Bearer ${token}`,
  };
};

export const fetchClientS3CredentialByProfile = async (
  profileName: string
): Promise<ClientS3CredentialDTO> => {
  const url = `${API_BASE_URL}/api/client-s3-credentials/profile/${encodeURIComponent(
    profileName
  )}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      ...getAuthHeaders(),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    throw new ClientS3ApiError(
      data?.message || "Failed to fetch S3 credentials",
      response.status
    );
  }

  return data.data as ClientS3CredentialDTO;
};

export const setupClientS3Credentials =
  async (): Promise<ClientS3CredentialDTO> => {
    const url = `${API_BASE_URL}/api/client-s3-credentials/setup`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new ClientS3ApiError(
        data?.message || "Failed to set up S3 credentials",
        response.status
      );
    }

    return data.data as ClientS3CredentialDTO;
  };

export const downloadClientS3Profile = async (
  profileName: string
): Promise<{ blob: Blob; fileName: string }> => {
  const url = `${API_BASE_URL}/api/client-s3-credentials/profile/${encodeURIComponent(
    profileName
  )}/download`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    let message = "Failed to download profile";
    try {
      const data = await response.json();
      message = data?.message || message;
    } catch (error) {
      // Ignore JSON parse error
    }
    throw new Error(message);
  }

  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="([^"]+)"/i);
  const fileName = match?.[1] || `${profileName}.duck`;

  const blob = await response.blob();
  return { blob, fileName };
};
